import React from 'react';
import { useAgent } from '../../../contexts/AgentContext';
import { useChatSidebarCollapseOptional } from '../../../contexts/ChatSidebarCollapseContext';
import { useConfirmationsOptional } from '../../../contexts/ConfirmationsContext';
import { ReactComponent as ArrowRightCircleIcon } from '../../../icons/arrow-right-circle.svg';
import { generateMessageColors } from '../sidebar/threadUtils';
import './collapsed-revealer.scss';

interface CollapsedRevealerProps {
  discussingObject?: string;
}

/**
 * CollapsedRevealer - A button that appears when the chat sidebar is collapsed
 * Shows agent avatar + "Dreamie chat" or "Campaign chat" + arrow icon
 * Clicking expands the sidebar
 * Shows urgency badge when there are pending confirmations
 */
const CollapsedRevealer: React.FC<CollapsedRevealerProps> = ({
  discussingObject,
}) => {
  const sidebarContext = useChatSidebarCollapseOptional();
  const { selectedAgent } = useAgent();
  const confirmationsContext = useConfirmationsOptional();

  // Don't render if sidebar is not collapsed or context is unavailable
  if (!sidebarContext?.isCollapsed) {
    return null;
  }

  const { expandSidebar } = sidebarContext;
  const hasConfirmations = confirmationsContext?.hasConfirmations ?? false;

  // Determine label based on discussing object
  const isCampaign = discussingObject?.toLowerCase() === 'campaign';
  const label = isCampaign ? 'Campaign chat' : 'Dreamie chat';

  // Generate colors from predominant color (like confirmation notifications)
  const colors = selectedAgent?.predominantColour
    ? generateMessageColors(selectedAgent.predominantColour)
    : null;

  // Box shadow changes when there are confirmations
  const boxShadow = hasConfirmations
    ? '0 8px 16px 0 rgba(6, 22, 54, 0.10), 0 2px 4px 0 rgba(0, 13, 38, 0.20)'
    : colors?.boxShadow;

  return (
    <>
      <button
        className={`collapsed-revealer${hasConfirmations ? ' has-confirmations' : ''}`}
        onClick={expandSidebar}
        style={
          colors
            ? {
                background: colors.background,
                borderColor: colors.borderColor,
                boxShadow,
              }
            : hasConfirmations
              ? { boxShadow }
              : undefined
        }
      >
        <div className='revealer-avatar'>
          <div className='avatar-image-wrapper'>
            {selectedAgent?.dreamieImageUrl ? (
              <img src={selectedAgent.dreamieImageUrl} alt='Agent' />
            ) : (
              <div className='avatar-fallback'>?</div>
            )}
          </div>
          {hasConfirmations && (
            <div className='urgency-icon'>
              <span>!</span>
            </div>
          )}
        </div>
        <span className='revealer-label'>{label}</span>
        <ArrowRightCircleIcon className='revealer-arrow' />
      </button>
      <div className='revealer-divider' />
    </>
  );
};

export default CollapsedRevealer;
